import { useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

export default function LanguageSwitcher({ className = "" }) {
  const { lang, setLang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const select = (next) => {
    setLang(next);
    setOpen(false);
  };

  return (
    <div ref={rootRef} className={`relative ${className}`}>
      <button
        type="button"
        data-testid="language-switcher"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className="inline-flex h-11 items-center gap-1.5 border border-black/15 px-3 text-foreground transition-colors duration-200 hover:border-primary hover:text-primary"
      >
        <span className="label text-[0.65rem]">{t(`lang.code.${lang}`)}</span>
        <ChevronDown className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          role="listbox"
          data-testid="language-menu"
          className="absolute right-0 top-full z-[60] mt-2 w-56 border border-black/10 bg-background shadow-sm"
        >
          <div className="border-b border-black/10 px-4 py-3">
            <span className="label text-muted-foreground">{t("lang.change")}</span>
          </div>
          {[
            { id: "en", labelKey: "lang.english" },
            { id: "hi", labelKey: "lang.hindi" },
          ].map((opt) => (
            <button
              key={opt.id}
              type="button"
              role="option"
              aria-selected={lang === opt.id}
              data-testid={`language-option-${opt.id}`}
              onClick={() => select(opt.id)}
              className="flex w-full items-center gap-3 px-4 py-3 text-left text-sm transition-colors duration-200 hover:bg-secondary"
            >
              <span
                className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                  lang === opt.id ? "border-primary" : "border-black/30"
                }`}
              >
                {lang === opt.id ? <span className="h-2 w-2 rounded-full bg-primary" /> : null}
              </span>
              <span className={lang === opt.id ? "text-foreground" : "text-muted-foreground"}>{t(opt.labelKey)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
